import { viewFromPath } from './routes'
import { BrandLoader } from '@/shared/white-label/BrandLoader'
import { BrandMark } from '@/shared/white-label/BrandMark'
import { useActiveBrand } from '@/shared/white-label/useBrand'

// A short stack of shimmer lines — the generic stand-in wherever a list or a
// small panel is loading and there is no distinctive silhouette worth mirroring.
// Widths are uneven so it reads as text rather than a progress bar.
const SKEL_ROW_WIDTHS = ['70%', '92%', '84%', '60%', '78%', '88%']

export function SkelRows({ rows = 4, label, className = '' }: {
  rows?: number
  label: string
  className?: string
}) {
  return (
    <div className={`flex flex-col gap-2.5 ${className}`} aria-busy="true" aria-label={label}>
      {Array.from({ length: rows }).map((_, i) => (
        <span key={i} className="skeleton h-3.5" style={{ width: SKEL_ROW_WIDTHS[i % SKEL_ROW_WIDTHS.length] }} />
      ))}
    </div>
  )
}

// Whole-app silhouette for the gap between page load and /api/me answering.
// On a cold Cloud Run instance that probe can take several seconds, which used
// to be a centred "Loading…" line on an empty page. The chrome is the same
// shape the app settles into, so nothing jumps when the real UI takes over.
//
// The logo is real rather than a grey box: it is a cached static asset, so the
// brand lands immediately and only the unknown parts shimmer. The body is the
// loader the view itself shows while its data loads, so it carries straight on
// when the app takes over.
export function BootSkeleton() {
  const brand = useActiveBrand()
  const mode = viewFromPath(window.location.pathname) ?? 'focus'
  return (
    <div className="app" data-surface="fog" data-brand={brand.key}>
      <div className="layout">
        <header className="topbar" aria-busy="true" aria-label="Laddar">
          <div className="flex items-center gap-3 justify-self-start">
            <BrandMark />
            <span className="max-[900px]:hidden font-display text-[10px] tracking-[0.14em] uppercase text-ink-500 border-l border-ink-200 pl-3">{brand.product}</span>
          </div>
          <nav className="inline-flex bg-transparent gap-1">
            {[64, 62, 92, 58].map((w, i) => (
              <span key={i} className="skeleton h-7" style={{ width: w, borderRadius: 6 }} />
            ))}
          </nav>
          <div className="flex items-center gap-3 justify-self-end">
            <span className="skeleton h-7" style={{ width: 84, borderRadius: 6 }} />
            <span className="skeleton" style={{ width: 28, height: 28, borderRadius: '50%' }} />
          </div>
        </header>
        <main className="bg-background overflow-hidden min-h-0 grid grid-cols-1">
          <div className="min-h-0 min-w-0 overflow-hidden flex flex-col">
            <ViewLoading label={mode === 'table' ? 'Laddar leads' : mode === 'focus' ? 'Laddar lead' : 'Laddar'} />
          </div>
        </main>
      </div>
    </div>
  )
}

// A whole view waiting for its first data: Focus and Tabell before the leads
// arrive, Översikt, Karta, Hantera and the admin tabs. The mark sits in the
// middle of the area that is loading.
export function ViewLoading({ label = 'Laddar' }: { label?: string }) {
  return <BrandLoader label={label} className="h-full min-h-[240px]" />
}
